import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { saveMsiOutboundBarraChoice } from '../utils/ApiCall';
import {
    watchlistInsetPanelSx,
    watchlistSemanticFrameSx,
} from '../styles/watchlistMsiStyles';

const DEFAULT_SOURCE_NAME = 'risk_barra_factor_boundary';

const normalizeText = (value) => {
    if (value == null) {
        return '';
    }
    return String(value).trim();
};

const normalizeUpperText = (value) => normalizeText(value).toUpperCase();

const normalizeCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
};

const normalizeCandidate = (candidate, index) => {
    const candidateData = candidate && typeof candidate === 'object' ? candidate : {};
    const normalized = {
        barrid: normalizeText(candidateData.barrid) || null,
        country: normalizeUpperText(candidateData.country) || null,
        ticker: normalizeText(candidateData.ticker) || null,
        exchange: normalizeText(candidateData.exchange) || null,
        mic: normalizeText(candidateData.mic) || null,
        currency: normalizeText(candidateData.currency) || null,
        sedol: normalizeUpperText(candidateData.sedol) || null,
        cusip: normalizeUpperText(candidateData.cusip) || null,
        isin: normalizeUpperText(candidateData.isin) || null,
        description: normalizeText(candidateData.description) || null,
    };

    const fingerprintParts = [
        normalized.sedol || '',
        normalized.cusip || '',
        normalized.isin || '',
        normalized.country || '',
    ];
    const hasFingerprintEvidence = fingerprintParts.some(Boolean);
    const fingerprint = hasFingerprintEvidence ? fingerprintParts.join('|') : null;

    const key = [
        normalized.barrid || '',
        fingerprint || '',
        normalized.ticker || '',
        String(index),
    ].join('::');

    return {
        ...normalized,
        _fingerprint: fingerprint,
        _key: key,
    };
};

const buildSearchableCandidateText = (candidate) => {
    return [
        candidate.barrid,
        candidate.country,
        candidate.ticker,
        candidate.exchange,
        candidate.mic,
        candidate.currency,
        candidate.sedol,
        candidate.cusip,
        candidate.isin,
        candidate.description,
    ].map((part) => normalizeText(part).toLowerCase()).join(' ');
};

const buildActionableRows = (identityResolution) => {
    const errors = Array.isArray(identityResolution?.errors) ? identityResolution.errors : [];
    return errors
        .map((error, errorIndex) => {
            const errorData = error && typeof error === 'object' ? error : {};
            const code = normalizeText(errorData.code).toLowerCase();
            if (code !== 'ambiguous_identifier') {
                return null;
            }

            const rawCandidates = Array.isArray(errorData.candidates) ? errorData.candidates : [];
            const candidates = rawCandidates
                .map((candidate, candidateIndex) => normalizeCandidate(candidate, candidateIndex))
                .filter((candidate) => Boolean(candidate.barrid));
            if (candidates.length === 0) {
                return null;
            }

            const cogniveId = normalizeText(errorData.cognive_id) || `unknown-${errorIndex}`;
            return {
                rowId: `${cogniveId}::${errorIndex}`,
                cogniveId,
                displayLabel: `Security ${errorIndex + 1}`,
                code,
                hint: normalizeText(errorData.hint) || '',
                ambiguityClass: normalizeText(errorData.ambiguity_class) || 'other',
                resolutionMode: normalizeText(errorData.resolution_mode),
                reviewRequired: Boolean(errorData.review_required),
                candidateCount: Math.max(
                    Number(errorData.candidate_count || 0),
                    candidates.length,
                ),
                candidates,
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.cogniveId.localeCompare(right.cogniveId));
};

export default function MsiOutboundBarraResolutionDialog({
    open,
    identityResolution = null,
    onClose,
    onSaveAndRetry,
}) {
    const [countryFilter, setCountryFilter] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [selectedByRowId, setSelectedByRowId] = useState({});
    const [savedByRowId, setSavedByRowId] = useState({});
    const [savingRowId, setSavingRowId] = useState('');
    const [savingAndRetry, setSavingAndRetry] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const actionableRows = useMemo(() => buildActionableRows(identityResolution), [identityResolution]);

    const countryOptions = useMemo(() => {
        return Array.from(
            new Set(
                actionableRows.flatMap((row) => row.candidates.map((candidate) => candidate.country).filter(Boolean)),
            ),
        ).sort();
    }, [actionableRows]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setCountryFilter('all');
        setSearchText('');
        setSelectedByRowId({});
        setSavedByRowId({});
        setSavingRowId('');
        setSavingAndRetry(false);
        setErrorMessage('');
        setSuccessMessage('');
    }, [open, identityResolution]);

    const filterCandidates = useCallback((candidates) => {
        const normalizedSearch = normalizeText(searchText).toLowerCase();
        return candidates.filter((candidate) => {
            if (countryFilter !== 'all' && normalizeUpperText(candidate.country) !== countryFilter) {
                return false;
            }
            if (!normalizedSearch) {
                return true;
            }
            return buildSearchableCandidateText(candidate).includes(normalizedSearch);
        });
    }, [countryFilter, searchText]);

    const getSelectedCandidateForRow = useCallback((row) => {
        const selectedKey = selectedByRowId[row.rowId];
        if (!selectedKey) {
            return null;
        }
        return row.candidates.find((candidate) => candidate._key === selectedKey) || null;
    }, [selectedByRowId]);

    const persistChoice = useCallback(async (row, candidate) => {
        const response = await saveMsiOutboundBarraChoice({
            sourceName: normalizeText(identityResolution?.source) || DEFAULT_SOURCE_NAME,
            cogniveId: row.cogniveId,
            selectedBarrid: candidate.barrid,
            selectedCountry: candidate.country,
            selectedFingerprint: candidate._fingerprint,
        });

        if (response?.success === false) {
            throw new Error(response?.message || `Failed to save choice for ${row.displayLabel}.`);
        }
    }, [identityResolution]);

    const handleSaveRowChoice = useCallback(async (row) => {
        const selectedCandidate = getSelectedCandidateForRow(row);
        if (!selectedCandidate) {
            setErrorMessage(`Select a candidate for ${row.displayLabel} before saving.`);
            return;
        }

        setSavingRowId(row.rowId);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            await persistChoice(row, selectedCandidate);
            setSavedByRowId((current) => ({
                ...current,
                [row.rowId]: true,
            }));
            setSuccessMessage(`Saved outbound Barra choice for ${row.displayLabel}.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : `Failed to save choice for ${row.displayLabel}.`);
        } finally {
            setSavingRowId('');
        }
    }, [getSelectedCandidateForRow, persistChoice]);

    const hasSelectedChoices = useMemo(
        () => actionableRows.some((row) => Boolean(getSelectedCandidateForRow(row))),
        [actionableRows, getSelectedCandidateForRow],
    );

    const handleSaveAndRetry = useCallback(async () => {
        const selectedRows = actionableRows
            .map((row) => ({
                row,
                candidate: getSelectedCandidateForRow(row),
            }))
            .filter((item) => Boolean(item.candidate));

        if (selectedRows.length === 0) {
            setErrorMessage('Select at least one candidate before retrying matrix load.');
            return;
        }

        setSavingAndRetry(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            for (const item of selectedRows) {
                await persistChoice(item.row, item.candidate);
            }
            setSavedByRowId((current) => {
                const next = { ...current };
                selectedRows.forEach(({ row }) => {
                    next[row.rowId] = true;
                });
                return next;
            });
            setSuccessMessage('Saved choices. Retrying factor matrix load.');
            if (onSaveAndRetry) {
                await onSaveAndRetry();
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save outbound Barra choices.');
        } finally {
            setSavingAndRetry(false);
        }
    }, [actionableRows, getSelectedCandidateForRow, onSaveAndRetry, persistChoice]);

    const fallbackCount = normalizeCount(identityResolution?.fallback_count);
    const actionableErrorCount = normalizeCount(identityResolution?.actionable_error_count) ?? actionableRows.length;

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (savingAndRetry || savingRowId) {
                    return;
                }
                onClose();
            }}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>Resolve Outbound Barra Mapping Ambiguities</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Ambiguous outbound Cognive-to-Barra mappings were detected. Choose the correct candidate(s), save choices, then retry matrix load.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Actionable Rows: ${actionableErrorCount}`}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Auto-mapped Rows: ${fallbackCount ?? 0}`}
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="outbound-country-filter-label">Country</InputLabel>
                            <Select
                                labelId="outbound-country-filter-label"
                                label="Country"
                                value={countryFilter}
                                onChange={(event) => setCountryFilter(event.target.value)}
                            >
                                <MenuItem value="all">All countries</MenuItem>
                                {countryOptions.map((country) => (
                                    <MenuItem key={country} value={country}>{country}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Search Evidence"
                            placeholder="ticker, country, exchange, description"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            fullWidth
                        />
                    </Stack>

                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                    {successMessage && <Alert severity="success">{successMessage}</Alert>}

                    {actionableRows.length === 0 ? (
                        <Alert severity="info">No actionable outbound ambiguity rows were returned.</Alert>
                    ) : (
                        <Stack spacing={1.5}>
                            {actionableRows.map((row) => {
                                const filteredCandidates = filterCandidates(row.candidates);
                                const isRowSaving = savingRowId === row.rowId;
                                return (
                                    <Box
                                        key={row.rowId}
                                        sx={[
                                            {
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 1.5,
                                            },
                                            watchlistInsetPanelSx,
                                        ]}
                                    >
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {row.displayLabel}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    class={row.ambiguityClass} mode={row.resolutionMode || '-'} candidates={row.candidateCount}
                                                    {row.reviewRequired ? ' review_required=true' : ''}
                                                </Typography>
                                                {row.hint && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {row.hint}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {savedByRowId[row.rowId] && <Chip size="small" color="success" label="Saved" />}
                                        </Stack>

                                        {filteredCandidates.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                                No candidates match current filters.
                                            </Typography>
                                        ) : (
                                            <RadioGroup
                                                value={selectedByRowId[row.rowId] || ''}
                                                onChange={(event) => {
                                                    const nextKey = normalizeText(event.target.value);
                                                    setSelectedByRowId((current) => ({
                                                        ...current,
                                                        [row.rowId]: nextKey,
                                                    }));
                                                }}
                                                sx={{ mt: 1.5 }}
                                            >
                                                {filteredCandidates.map((candidate) => (
                                                    <Box
                                                        key={`${row.rowId}-${candidate._key}`}
                                                        sx={[
                                                            {
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                px: 1,
                                                                py: 0.75,
                                                                mb: 1,
                                                            },
                                                            watchlistSemanticFrameSx(
                                                                selectedByRowId[row.rowId] === candidate._key
                                                                    ? 'info'
                                                                    : 'neutral',
                                                            ),
                                                        ]}
                                                    >
                                                        <FormControlLabel
                                                            value={candidate._key}
                                                            control={<Radio size="small" />}
                                                            label={(
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {candidate.description || candidate.ticker || 'Candidate'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                        country={candidate.country || '-'} ticker={candidate.ticker || '-'} exchange={candidate.exchange || '-'} mic={candidate.mic || '-'} currency={candidate.currency || '-'}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            sx={{ alignItems: 'flex-start', m: 0 }}
                                                        />
                                                    </Box>
                                                ))}
                                            </RadioGroup>
                                        )}

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={
                                                    savingAndRetry
                                                    || isRowSaving
                                                    || !getSelectedCandidateForRow(row)
                                                }
                                                onClick={() => {
                                                    void handleSaveRowChoice(row);
                                                }}
                                            >
                                                {isRowSaving ? 'Saving...' : 'Save Choice'}
                                            </Button>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={savingAndRetry || Boolean(savingRowId)}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    disabled={savingAndRetry || Boolean(savingRowId) || !hasSelectedChoices}
                    onClick={() => {
                        void handleSaveAndRetry();
                    }}
                    startIcon={savingAndRetry ? <CircularProgress color="inherit" size={16} /> : null}
                >
                    {savingAndRetry ? 'Saving...' : 'Save and Retry'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

MsiOutboundBarraResolutionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    identityResolution: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaveAndRetry: PropTypes.func,
};
